import React from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { formatDateShort } from './formatters';
import type { LifecyclePrediction } from './types';
import { digitalTwinChartColors as colors } from './digitalTwinChartColors';

interface GrowthCurveChartProps {
  prediction: LifecyclePrediction;
  className?: string;
  showLegend?: boolean;
}

interface ChartRow {
  date: string;
  dateFormatted: string;
  actualKg: number | null;
  estimatedKg: number | null;
  projectedKg: number | null;
}

const formatAxisKg = (value: number): string => String(Math.round(value));

const chartYAxisMax = (targetWeightKg: number): number => {
  const padded = targetWeightKg * 1.08;
  return Math.max(40, Math.ceil(padded / 30) * 30);
};

const circleDot = (fill: string, r = 2.5) => ({
  r,
  fill,
  stroke: '#ffffff',
  strokeWidth: 1
});

interface TimelineMarker {
  x: string;
  stroke: string;
  strokeDasharray?: string;
  labelLines: string[];
  labelPosition: 'top' | 'insideTopLeft';
}

interface StackedMarkerLabelProps {
  viewBox?: { x?: number; y?: number };
  lines: string[];
  fill: string;
  labelPosition: TimelineMarker['labelPosition'];
}

const STACKED_LABEL_LINE_HEIGHT = 14;

const StackedMarkerLabel: React.FC<StackedMarkerLabelProps> = ({ viewBox, lines, fill, labelPosition }) => {
  if (viewBox?.x == null || viewBox.y == null || lines.length === 0) {
    return null;
  }

  const anchor = labelPosition === 'insideTopLeft' ? 'start' : 'middle';
  const labelX = labelPosition === 'insideTopLeft' ? viewBox.x + 6 : viewBox.x;
  const labelY = viewBox.y - 4;

  return (
    <text x={labelX} y={labelY} textAnchor={anchor} fill={fill} fontSize={10} fontWeight={600}>
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={labelX} dy={index === 0 ? 0 : STACKED_LABEL_LINE_HEIGHT}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

const DiamondDot = (props: { cx?: number; cy?: number; fill?: string }) => {
  const { cx, cy, fill } = props;
  if (cx == null || cy == null) return null;
  return (
    <path
      d={`M${cx},${cy - 3} L${cx + 3},${cy} L${cx},${cy + 3} L${cx - 3},${cy} Z`}
      fill={fill ?? colors.projected}
      stroke="#ffffff"
      strokeWidth={1}
    />
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as ChartRow | undefined;
  if (!row) return null;

  return (
    <div
      style={{
        padding: '10px 14px',
        backgroundColor: colors.tooltipBg,
        border: `1px solid ${colors.tooltipBorder}`,
        borderRadius: 8,
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
        fontSize: 12
      }}
    >
      <div style={{ marginBottom: 6, color: colors.axis, fontWeight: 600 }}>{label}</div>
      {row.actualKg != null && (
        <div style={{ marginBottom: 2 }}>
          {t('digitalTwin.chart.actual')}: <strong>{row.actualKg.toFixed(1)}</strong> {t('digitalTwin.units.kg')}
        </div>
      )}
      {row.estimatedKg != null && (
        <div style={{ marginBottom: 2 }}>
          {t('digitalTwin.chart.estimated')}: <strong>{row.estimatedKg.toFixed(1)}</strong> {t('digitalTwin.units.kg')}
        </div>
      )}
      {row.projectedKg != null && (
        <div>
          {t('digitalTwin.chart.projected')}: <strong>{row.projectedKg.toFixed(1)}</strong> {t('digitalTwin.units.kg')}
        </div>
      )}
    </div>
  );
};

export const GrowthCurveChart: React.FC<GrowthCurveChartProps> = ({ prediction, className, showLegend = true }) => {
  const { t } = useTranslation();

  const chartData = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return prediction.growthCurve.map((point) => {
      const pointDate = new Date(point.date);
      pointDate.setHours(0, 0, 0, 0);
      const isFuture = pointDate > today;
      const isActual = point.isActual;
      const measuredKg = point.measuredWeightKg ?? (isActual ? point.weightKg : null);
      const modelKg = point.weightKg;

      return {
        date: point.date,
        dateFormatted: formatDateShort(point.date),
        actualKg: isActual ? measuredKg : null,
        estimatedKg: !isFuture ? modelKg : null,
        projectedKg: isFuture ? modelKg : null
      };
    });
  }, [prediction.growthCurve]);

  const chartCategoryLabels = React.useMemo(() => new Set(chartData.map((row) => row.dateFormatted)), [chartData]);

  const timelineMarkers = React.useMemo(() => {
    const assumedSuffix = ` ${t('digitalTwin.chart.assumedByModel')}`;
    const grouped = new Map<
      string,
      {
        milestones: string[];
        treatments: string[];
        stroke: string;
        strokeDasharray?: string;
        hasMilestone: boolean;
      }
    >();

    const addMilestone = (x: string, label: string, stroke: string, strokeDasharray?: string) => {
      if (!chartCategoryLabels.has(x)) {
        return;
      }

      const existing = grouped.get(x);
      if (existing) {
        if (!existing.milestones.includes(label)) {
          existing.milestones.push(label);
        }
        existing.stroke = stroke;
        existing.strokeDasharray = strokeDasharray;
        existing.hasMilestone = true;
        return;
      }

      grouped.set(x, {
        milestones: [label],
        treatments: [],
        stroke,
        strokeDasharray,
        hasMilestone: true
      });
    };

    const addTreatment = (x: string, supplyName: string) => {
      if (!chartCategoryLabels.has(x)) {
        return;
      }

      const existing = grouped.get(x);
      if (existing) {
        if (!existing.treatments.includes(supplyName)) {
          existing.treatments.push(supplyName);
        }
        return;
      }

      grouped.set(x, {
        milestones: [],
        treatments: [supplyName],
        stroke: colors.injection,
        strokeDasharray: '3 3',
        hasMilestone: false
      });
    };

    const milestones = [
      {
        x: formatDateShort(prediction.birth.date),
        shortLabel: t('digitalTwin.milestones.birth'),
        stroke: colors.milestoneBirth,
        isEstimated: prediction.birth.isEstimated
      },
      {
        x: formatDateShort(prediction.weaned.date),
        shortLabel: t('digitalTwin.milestones.weaned'),
        stroke: colors.milestoneWeaned,
        isEstimated: prediction.weaned.isEstimated
      },
      {
        x: formatDateShort(prediction.slaughterReady.date),
        shortLabel: t('digitalTwin.milestones.slaughterReady'),
        stroke: colors.milestoneSlaughter,
        isEstimated: prediction.slaughterReady.isEstimated
      }
    ];

    for (const milestone of milestones) {
      addMilestone(
        milestone.x,
        `${milestone.shortLabel}${milestone.isEstimated ? assumedSuffix : ''}`,
        milestone.stroke,
        milestone.isEstimated ? '4 4' : undefined
      );
    }

    for (const withdrawal of prediction.antibioticImpact?.activeWithdrawals ?? []) {
      addTreatment(formatDateShort(withdrawal.injectionDate), withdrawal.supplyName);
    }

    return Array.from(grouped.entries()).map(([x, group]): TimelineMarker => {
      const treatmentLine =
        group.treatments.length > 1
          ? t('digitalTwin.chart.antibioticTreatmentCount', {
              count: group.treatments.length,
              defaultValue: '{{count}} antibiotic treatments'
            })
          : group.treatments[0];

      const labelLines = [...group.milestones, ...(treatmentLine ? [treatmentLine] : [])];

      return {
        x,
        stroke: group.hasMilestone ? group.stroke : colors.injection,
        strokeDasharray: group.strokeDasharray,
        labelLines,
        labelPosition: group.hasMilestone ? 'top' : 'insideTopLeft'
      };
    });
  }, [chartCategoryLabels, prediction, t]);

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData} margin={{ top: 52, right: 24, left: 12, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
          <XAxis
            dataKey="dateFormatted"
            tick={{ fontSize: 11, fill: colors.axis }}
            axisLine={{ stroke: colors.grid }}
            tickLine={false}
            tickMargin={8}
          />
          <YAxis
            tick={{ fontSize: 11, fill: colors.axis }}
            axisLine={false}
            tickLine={false}
            domain={[0, chartYAxisMax(prediction.targetWeightKg)]}
            allowDecimals={false}
            tickFormatter={formatAxisKg}
            tickMargin={4}
            label={{
              value: t('digitalTwin.units.kg'),
              angle: -90,
              position: 'insideLeft',
              style: { fill: colors.axis, fontSize: 11, fontWeight: 600 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="line"
              iconSize={12}
              formatter={(value) => {
                const key =
                  value === 'Actual'
                    ? 'digitalTwin.chart.actual'
                    : value === 'Estimated'
                      ? 'digitalTwin.chart.estimated'
                      : value === 'Projected'
                        ? 'digitalTwin.chart.projected'
                        : 'digitalTwin.chart.target';
                return t(key);
              }}
            />
          )}
          <ReferenceLine
            y={prediction.targetWeightKg}
            stroke={colors.target}
            strokeDasharray="5 5"
            strokeWidth={1.5}
            ifOverflow="extendDomain"
            label={{
              value: `${t('digitalTwin.chart.target')} (${prediction.targetWeightKg.toFixed(0)} kg)`,
              position: 'insideLeft',
              fill: colors.axis,
              fontSize: 11,
              fontWeight: 600,
              dy: -6,
              dx: 8
            }}
          />
          {timelineMarkers.map((marker) => (
            <ReferenceLine
              key={`${marker.x}-${marker.labelLines.join('|')}`}
              x={marker.x}
              stroke={marker.stroke}
              strokeWidth={1.25}
              strokeDasharray={marker.strokeDasharray}
              strokeOpacity={0.85}
              label={{
                position: marker.labelPosition,
                content: (labelProps) => (
                  <StackedMarkerLabel
                    viewBox={labelProps.viewBox as { x?: number; y?: number } | undefined}
                    lines={marker.labelLines}
                    fill={marker.stroke}
                    labelPosition={marker.labelPosition}
                  />
                )
              }}
            />
          ))}
          <Line
            type="monotone"
            dataKey="estimatedKg"
            stroke={colors.estimated}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={circleDot(colors.estimated)}
            connectNulls
            isAnimationActive={false}
            name="Estimated"
          />
          <Line
            type="monotone"
            dataKey="projectedKg"
            stroke={colors.projected}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={(props) => <DiamondDot {...props} fill={colors.projected} />}
            connectNulls
            isAnimationActive={false}
            name="Projected"
          />
          <Line
            type="monotone"
            dataKey="actualKg"
            stroke="none"
            strokeWidth={0}
            dot={circleDot(colors.actual, 3)}
            activeDot={{ r: 4, fill: colors.actual, stroke: '#ffffff', strokeWidth: 1 }}
            connectNulls={false}
            isAnimationActive={false}
            name="Actual"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
